import React, { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { TafLogo } from "@/components/Brand";
import { Printer, ArrowLeft, FileDown } from "lucide-react";
import { BTN_PRIMARY, BTN_GHOST } from "@/lib/ui";
import { exportElementToPdf, pdfStamp } from "@/lib/pdf";
import { byEmployee } from "@/lib/sortEmployees";

const DAYS = [["1", "Δευτέρα"], ["2", "Τρίτη"], ["3", "Τετάρτη"], ["4", "Πέμπτη"],
  ["5", "Παρασκευή"], ["6", "Σάββατο"], ["0", "Κυριακή"]];

const hoursOf = (days) => DAYS.reduce((sum, [d]) => {
  const v = days?.[d];
  if (!v || v.off || !v.start || !v.end) return sum;
  const [sh, sm] = v.start.split(":").map(Number);
  const [eh, em] = v.end.split(":").map(Number);
  return sum + Math.max(0, (eh * 60 + em - sh * 60 - sm) / 60);
}, 0);

export default function SchedulePrint() {
  const sheetRef = React.useRef(null);
  const [pdfBusy, setPdfBusy] = React.useState(false);
  const downloadPdf = async () => {
    setPdfBusy(true);
    try {
      await exportElementToPdf(sheetRef.current, `TAF_Programma_${pdfStamp()}.pdf`, "landscape");
    } finally {
      setPdfBusy(false);
    }
  };
  const [params] = useSearchParams();
  const week = params.get("week") || "";
  const { data: employees = [] } = useQuery({ queryKey: ["employees"], queryFn: async () => (await api.get("/employees")).data });
  const { data: weekData } = useQuery({
    queryKey: ["schedule-week-print", week],
    queryFn: async () => (await api.get(`/schedules/week${week ? `?week_start=${week}` : ""}`)).data,
  });
  const schedules = weekData?.rows || [];

  const rows = useMemo(() => {
    const map = {};
    schedules.forEach((s) => { map[s.employee_name] = s.days; });
    return employees
      .filter((e) => map[e.name])
      .map((e) => ({ ...e, days: map[e.name], hours: Math.round(hoursOf(map[e.name])) }))
      .sort(byEmployee);
  }, [employees, schedules]);

  const byDept = rows.reduce((acc, r) => {
    (acc[r.department || "—"] = acc[r.department || "—"] || []).push(r);
    return acc;
  }, {});
  const totalHours = rows.reduce((s, r) => s + r.hours, 0);
  const today = new Date().toLocaleDateString("el-GR", { day: "2-digit", month: "long", year: "numeric" });

  return (
    <div className="bg-white print-area print:p-0" data-testid="schedule-print-page">
      <div className="flex items-center justify-between gap-3 mb-6 no-print">
        <Link to="/schedule" data-testid="print-back" className={BTN_GHOST}>
          <ArrowLeft className="h-4 w-4" /> Πίσω στο πρόγραμμα
        </Link>
        <div className="flex items-center gap-3">
          <button data-testid="print-pdf" onClick={downloadPdf} disabled={pdfBusy} className={BTN_GHOST}>
            <FileDown className="h-4 w-4" /> {pdfBusy ? "Δημιουργία…" : "Λήψη PDF"}
          </button>
          <button data-testid="print-now" onClick={() => window.print()} className={BTN_PRIMARY}>
            <Printer className="h-4 w-4" /> Εκτύπωση
          </button>
        </div>
      </div>
      <div ref={sheetRef} className="bg-white">

      <div className="flex items-end justify-between gap-4 border-b-2 border-[#45423C] pb-4">
        <div className="flex items-center gap-3">
          <div>
            <TafLogo height={46} />
            <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#8A8577] mt-2">
              Εβδομαδιαίο Πρόγραμμα Βαρδιών {weekData ? `· ${weekData.week_start} – ${weekData.week_end}` : ""}
            </div>
          </div>
        </div>
        <div className="text-right text-xs text-[#6F6A60]">
          <div>Εκδόθηκε: {today}</div>
          <div>{rows.length} υπάλληλοι · {totalHours} ώρες / εβδομάδα</div>
        </div>
      </div>

      {Object.entries(byDept).map(([dept, list]) => (
        <div key={dept} className="mt-6 break-inside-avoid" data-testid={`print-dept-${dept}`}>
          <h2 className="font-serif text-lg text-[#45423C] border-b border-[#E3DED4] pb-1 mb-2">
            {dept} <span className="text-xs font-sans text-[#6F6A60]">({list.length})</span>
          </h2>
          <table className="w-full text-[11px] border-collapse">
            <thead>
              <tr className="bg-[#F1EDE6] text-left uppercase tracking-wider text-[#6F6A60]">
                <th className="border border-[#E3DED4] px-2 py-1.5">Υπάλληλος</th>
                <th className="border border-[#E3DED4] px-2 py-1.5">Ρόλος</th>
                {DAYS.map(([d, l]) => {
                  const dt = (weekData?.dates || []).find((x) => x.dow === d)?.date;
                  return (
                    <th key={d} className="border border-[#E3DED4] px-2 py-1.5 text-center">
                      {l.slice(0, 3)}
                      {dt && <span className="block text-[9px] font-normal text-[#8A8577] tabular">{dt.slice(8, 10)}/{dt.slice(5, 7)}</span>}
                    </th>
                  );
                })}
                <th className="border border-[#E3DED4] px-2 py-1.5 text-right">Ώρες</th>
              </tr>
            </thead>
            <tbody>
              {list.map((r) => (
                <tr key={r.id} data-testid={`print-row-${r.name}`}>
                  <td className="border border-[#E3DED4] px-2 py-1.5 font-semibold text-[#45423C]">{r.name}</td>
                  <td className="border border-[#E3DED4] px-2 py-1.5 text-[#6F6A60]">{r.position || "—"}</td>
                  {DAYS.map(([d]) => {
                    const v = r.days[d] || {};
                    const off = v.off !== false || !v.start;
                    return (
                      <td key={d} className={`border border-[#E3DED4] px-2 py-1.5 text-center tabular ${off ? "text-[#B9B0A2]" : "font-semibold text-[#45423C]"}`}>
                        {off ? "Ρεπό" : `${v.start}–${v.end}`}
                      </td>
                    );
                  })}
                  <td className="border border-[#E3DED4] px-2 py-1.5 text-right tabular font-semibold">{r.hours}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ))}

      {rows.length === 0 && (
        <p className="mt-8 text-center text-sm text-[#6F6A60]">Δεν υπάρχει καταχωρημένο πρόγραμμα.</p>
      )}

      <p className="mt-8 text-[10px] text-[#6F6A60] border-t border-[#E3DED4] pt-3">
        Το πρόγραμμα επαναλαμβάνεται κάθε εβδομάδα. Αλλαγές μόνο μετά από έγκριση HR ή Ιδιοκτήτη.
        Υπενθύμιση: έναρξη βάρδιας 15′ · λήξη 30′.
      </p>
      </div>
    </div>
  );
}
